import { z } from 'zod'

export const SpotInStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  address: z.string().optional(),
  zip: z.string().optional(),
  image: z.string().nullable(),
})

export const StampInSheetDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
  point: z.number(),
  spots: z.array(SpotInStampDataSchema).default([]),
})

/** シート開放（購入連動）に必要な商品情報（#3281 COURSE_UNLOCK） */
export const SheetUnlockDataSchema = z.object({
  productId: z.number(),
  price: z.number(),
  productName: z.string(),
})
export type SheetUnlockData = z.infer<typeof SheetUnlockDataSchema>

export const SheetDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(['stamp_rally', 'point_rally', 'point_card', 'overlay', 'none']),
  backgroundImage: z.string().nullable(),
  totalCount: z.number(),
  /** 開放日時（startAt）ベースの未開放ロック */
  isLocked: z.boolean().optional().default(false),
  startAt: z.string().nullable().optional(),
  /** 購入連動アクセス制御によるロック（対象商品未購入で内容が隠される） */
  locked: z.boolean().optional().default(false),
  /**
   * シート設定「詳細ページを公開する」の値 (#3365)。
   * false かつ locked のとき、このシート経由のスポットは詳細ページ（GET /api/v2/spots/{id}）が 404 になる。
   * 詳細遷移可否は (locked === false || spotDetailVisibleWhenLocked) で判定する。
   */
  spotDetailVisibleWhenLocked: z.boolean().optional().default(false),
  /** ロック解除に必要な商品情報（対象商品が無い場合は null） */
  unlock: SheetUnlockDataSchema.nullable().optional().default(null),
  stamps: z.array(StampInSheetDataSchema),
})

export const SheetGetResponseSchema = z.object({
  sheet: SheetDataSchema.nullable(),
})

export const SheetListResponseSchema = z.object({
  sheets: z.array(SheetDataSchema),
})

export type SpotInStampData = z.infer<typeof SpotInStampDataSchema>
export type StampInSheetData = z.infer<typeof StampInSheetDataSchema>
export type SheetData = z.infer<typeof SheetDataSchema>
export type SheetGetResponse = z.infer<typeof SheetGetResponseSchema>
export type SheetListResponse = z.infer<typeof SheetListResponseSchema>

/** history 1件（スタンプ獲得履歴） */
export const UserSheetHistoryDataSchema = z.object({
  id: z.number(),
  stampId: z.number(),
  name: z.string(),
  image: z.string().nullable(),
  overlayImage: z.string().nullable(),
  acquiredCount: z.number(),
  /** 獲得時点の実際のポイント値。バックエンドが対応していない旧データは null になる */
  point: z.number().nullish(),
  createdAt: z.string().nullable(),
  /**
   * 遷移先スポット ID（#2972）。バックエンドが「獲得したスポットが公開中ならその ID、
   * 獲得スポットの記録が無ければスタンプに紐づく公開中スポットの最若 ID、それも無ければ null」まで解決済み。
   * null のときはポイント履歴の行をタップ不可にする。
   * 旧バックエンド（ロールバック時）はキー自体を返さないため nullish（.default(null) にすると出力型で必須になり
   * 既存テストの履歴リテラルが型エラーになる）。利用側で `?? null` に倒す。
   */
  spotId: z.number().nullish(),
})

/** シート種別ごとの集計データ */
export const UserSheetMetaDataSchema = z.object({
  type: z.string().optional(),
  totalStampCount: z.number().nullish(),
  acquiredStampCount: z.number().nullish(),
  totalPoint: z.number().nullish(),
})

/** ユーザー向けシートデータ（SheetData に meta / history を追加）
 *
 * GET /api/v2/user/sheets は stamps を含まないため optional.default([]) で上書き。
 * useHomeStateMachine で GET /api/v2/sheets の stamps とマージして補完する。
 */
export const UserSheetDataSchema = SheetDataSchema.extend({
  stamps: z.array(StampInSheetDataSchema).optional().default([]),
  meta: UserSheetMetaDataSchema.nullable().optional(),
  history: z.array(UserSheetHistoryDataSchema).optional().default([]),
})

export const UserSheetListResponseSchema = z.object({
  sheets: z.array(UserSheetDataSchema),
})

export type UserSheetHistoryData = z.infer<typeof UserSheetHistoryDataSchema>
export type UserSheetMetaData = z.infer<typeof UserSheetMetaDataSchema>
export type UserSheetData = z.infer<typeof UserSheetDataSchema>
export type UserSheetListResponse = z.infer<typeof UserSheetListResponseSchema>
